import { supabase } from '../supabaseClient';
import type { Deliverable } from '@shared/types';

export interface SavedScope {
  id: string;
  userId: string;
  name: string;
  description?: string;
  deliverables: Deliverable[];
  createdAt: string;
  updatedAt: string;
}

export const scopeService = {
  async getScopes(): Promise<SavedScope[]> {
    const { data, error } = await supabase
      .from('scopes')
      .select('*')
      .order('name', { ascending: true });

    if (error) throw error;

    return (data || []).map((row) => ({
      id: row.id,
      userId: row.user_id,
      name: row.name,
      description: row.description,
      deliverables: row.deliverables || [],
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    }));
  },

  async getScope(id: string): Promise<SavedScope> {
    const { data, error } = await supabase
      .from('scopes')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;

    return {
      id: data.id,
      userId: data.user_id,
      name: data.name,
      description: data.description,
      deliverables: data.deliverables || [],
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  async createScope(params: {
    name: string;
    description?: string;
    deliverables: Deliverable[];
  }): Promise<SavedScope> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('scopes')
      .insert({
        user_id: user.id,
        name: params.name,
        description: params.description,
        deliverables: params.deliverables,
      })
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      userId: data.user_id,
      name: data.name,
      description: data.description,
      deliverables: data.deliverables || [],
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  async updateScope(
    id: string,
    params: {
      name?: string;
      description?: string;
      deliverables?: Deliverable[];
    }
  ): Promise<SavedScope> {
    const { data, error } = await supabase
      .from('scopes')
      .update({
        name: params.name,
        description: params.description,
        deliverables: params.deliverables,
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      userId: data.user_id,
      name: data.name,
      description: data.description,
      deliverables: data.deliverables || [],
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  async deleteScope(id: string): Promise<void> {
    const { error } = await supabase
      .from('scopes')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

